import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { Settings } from '../types';

/**
 * Kompromis brzina–popunjenost: prag popunjenosti i dozvoljeno čekanje.
 *
 * CLAUDE.md: „vidljiv i podesiv, nikad skriven u kodu". Do 15.10.2026. su
 * vrednosti stajale u bazi i menjao ih je samo administrator (analiza 2.0, B3).
 * Opseg proverava server; ovde se samo upisuje i kaže šta je odbijeno.
 */
const POLJA: { key: string; naziv: string; jedinica: string; objasnjenje: string }[] = [
  {
    key: 'popunjenost_threshold',
    naziv: 'Prag popunjenosti',
    jedinica: '%',
    objasnjenje: 'Ispod ovoga tura traži upisan razlog pre puštanja. Viši prag = punija vozila, sporija isporuka.'
  },
  {
    key: 'max_wait_days_interni',
    naziv: 'Čekanje — interni prenosi',
    jedinica: 'dana',
    objasnjenje: 'Posle ovoliko dana roba za objekat dobija oznaku „kasni" i ide prva, i uz nisku popunjenost.'
  },
  {
    key: 'max_wait_days_b2c',
    naziv: 'Čekanje — kupci',
    jedinica: 'dana',
    objasnjenje: 'Isto, za isporuke kupcima.'
  }
];

export default function PlanningSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [unos, setUnos] = useState<Record<string, string>>({});
  const [poruka, setPoruka] = useState<string | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await api.settings();
      setSettings(s);
      setUnos(Object.fromEntries(POLJA.map((p) => [p.key, s[p.key]?.value ?? ''])));
    } catch (e) {
      setGreska(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const sacuvaj = async (key: string, naziv: string) => {
    setBusy(key);
    setGreska(null);
    setPoruka(null);
    try {
      const r = await api.updateSetting(key, unos[key] ?? '');
      setPoruka(`${naziv}: sačuvano ${r.value}. Ostali ekrani ga vide pri sledećem osvežavanju.`);
      await load();
    } catch (e) {
      setGreska(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="panel">
      <h2 id="kompromis">Brzina ili popunjenost</h2>
      <p className="note">
        Ova tri broja određuju kad tura sme da krene poluprazna. Menja ih operater; svaka izmena
        se beleži sa imenom.
      </p>

      {greska && <div className="health health--bad">{greska}</div>}
      {poruka && <div className="health health--ok">{poruka}</div>}

      {settings && POLJA.map((p) => (
        <div className="field" key={p.key}>
          <label htmlFor={`podesavanje-${p.key}`}>{p.naziv} ({p.jedinica})</label>
          <input
            id={`podesavanje-${p.key}`}
            type="text"
            inputMode="decimal"
            value={unos[p.key] ?? ''}
            onChange={(e) => setUnos((u) => ({ ...u, [p.key]: e.target.value }))}
          />
          <button
            type="button"
            disabled={busy !== null || (unos[p.key] ?? '') === (settings[p.key]?.value ?? '')}
            onClick={() => void sacuvaj(p.key, p.naziv)}
          >
            {busy === p.key ? 'Čuvam…' : 'Sačuvaj'}
          </button>
          <p className="muted">{p.objasnjenje}</p>
        </div>
      ))}
    </section>
  );
}
